import c from 'classnames';
import { Suspense, useCallback, useRef, useState } from 'react';
import {
  matchPath,
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
} from 'react-router-dom';

import { BackButton } from '@repo/ui';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useApplyScrollIntent } from 'hooks/useApplyScrollIntent';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { useScrollDisplay } from 'hooks/useScrollDisplay';
import useAccountsAndKits from 'services/account/useAccountsAndKits';

import {
  ContainerSizes,
  Header,
  PageContainer,
  Sidebar,
  Spinfinity,
} from 'components';
import ResultsAnnouncementBanner from 'components/ResultsAnnouncementBanner/ResultsAnnouncementBanner';
import useFeature from 'services/features/useFeature';
import MobileNav from '../../MobileNav/MobileNav';
import { TinyAI, TinyAIProvider } from '../../TinyAI';

import styles from './ResponsiveLayout.module.css';

// Screens of the kit activation flow. The mobile bottom nav is hidden on all of
// them, and they leave the LG-capped container so their back button sits next
// to the sidebar, including when they are opened from Home or Results. Mirrors
// the route definitions in routes/THRoutes.tsx, views/Kits/KitsPage.tsx and
// views/Profiles/Profile.tsx. The bare '/profile/:kitId' is left out on
// purpose: it only redirects to the results page.
const ACTIVATION_FLOW_ROUTES = [
  '/kits/activate/:kitId',
  '/information/:kitId',
  '/information/:kitId/complete',
  '/profile',
  '/profile/:kitId/view',
  '/profile/:kitId/confirm',
  '/profile/:kitId/confirm/:questionGroupId',
  '/profile/:kitId/edit',
  '/profile/:kitId/edit/:questionGroupId',
  '/typeform/:tinyAccountId/:kitId/:typeformId',
  '/typeform/:tinyAccountId/:kitId/:typeformId/:surveyType',
];

export const PageAnalytics = ({ children }: { children: React.ReactNode }) => {
  useAnalytics().pageEvent();
  return <>{children}</>;
};
const ResponsiveLayout = () => {
  useAccountsAndKits();
  const { currentTinyAccount } = useCurrentKitAndTinyAccount();
  const navigate = useNavigate();
  const appWrapperRef = useRef<HTMLDivElement>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const toggleMobileMenu = useCallback(() => setIsMobileMenuOpen(o => !o), []);
  const { pathname } = useLocation();
  const { isFeatureEnabled: showResultsV2Banner } =
    useFeature('results_v2_banner');
  const { isFeatureEnabled: isRedesignedActivationEnabled } = useFeature(
    'redesigned_kit_activation_flow',
  );
  const isActivationFlowRoute = ACTIVATION_FLOW_ROUTES.some(route =>
    matchPath(route, pathname),
  );
  const hideMobileNav = isRedesignedActivationEnabled && isActivationFlowRoute;

  const isNewAllMetricsPage = !!useMatch('/results/:kitId/metrics/all-metrics');
  const isConditionsHomePage = !!useMatch('/results/:kitId/conditions/metrics');
  useApplyScrollIntent(appWrapperRef);
  const isResultsKitRoute = !!useMatch('/results/:kitId/*');
  const isActionPlanPage = useMatch('/action-plan/:kitId/*');
  const isTopLevelResults = useMatch({ path: '/results/:kitId', end: true });
  const isTopLevelActionPlan = useMatch({
    path: '/action-plan/:kitId',
    end: true,
  });

  const isBackButtonVisible = useScrollDisplay({
    resetOnNavigate: true,
    threshold: 50,
    action: 'show',
    lockStateOnThreshold: true,
    scrollableElRef: appWrapperRef,
  });

  const goBack = () => navigate(-1);

  const showBackButton =
    (isResultsKitRoute || isActionPlanPage) &&
    !(isTopLevelResults || isTopLevelActionPlan) &&
    isBackButtonVisible &&
    !isNewAllMetricsPage &&
    // Renders its own inline one, so this would float over the organ selector.
    !isConditionsHomePage;

  return (
    <TinyAIProvider>
      <div className={styles.rootContainer}>
        <div className={styles.sidebar}>
          <Sidebar />
        </div>
        <div className={styles.appWrapper} ref={appWrapperRef} id='appWrapper'>
          <div className={styles.contentContainer} id='content-container'>
            <PageContainer
              size={hideMobileNav ? ContainerSizes.AUTO : ContainerSizes.LG}
              className={styles.pageContainer}
            >
              {/* Render header for results pages */}
              {showBackButton && (
                <div
                  className={c(
                    styles.floatingBackButtonContainer,
                    'hide-on-desktop',
                  )}
                >
                  <BackButton variant='button' goBack={goBack} />
                </div>
              )}

              {isResultsKitRoute && isTopLevelResults && (
                <>
                  {showResultsV2Banner && (
                    <div className={styles.banner}>
                      <ResultsAnnouncementBanner
                        mainText='New look, same results'
                        secondaryText='Read our blog on how the summary page has changed.'
                        linkText='Read now'
                        linkUrl='https://www.tinyhealth.com/blog/unlock-your-microbiome-insights-with-our-new-results-view'
                      />
                    </div>
                  )}
                  <Header
                    type='full'
                    title={`${currentTinyAccount?.first_name}'s results`}
                    containerRef={appWrapperRef}
                  />
                </>
              )}
              <Suspense
                fallback={
                  <div className={styles.spinfinityWrapper}>
                    <Spinfinity />
                  </div>
                }
              >
                <PageAnalytics>
                  <div className={styles.contentWrapper}>
                    <Outlet />
                  </div>
                </PageAnalytics>
              </Suspense>
            </PageContainer>
          </div>
        </div>
        {!hideMobileNav && (
          <MobileNav isOpen={isMobileMenuOpen} onToggle={toggleMobileMenu} />
        )}
        <TinyAI scrollableElRef={appWrapperRef} isHidden={isMobileMenuOpen} />
      </div>
    </TinyAIProvider>
  );
};
export default ResponsiveLayout;
